import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaStar, FaCheck, FaShoppingCart, FaArrowLeft, FaSpinner, FaBolt } from "react-icons/fa";
import Swal from 'sweetalert2';
import SummaryApi from '../common'; 

const ProductDetails = () => {
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState('');
  
  // Cart specific states
  const [isInCart, setIsInCart] = useState(false);
  const [cartId, setCartId] = useState(null);
  const [updatingCart, setUpdatingCart] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("user_id");

  // --- TOAST CONFIGURATION ---
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer)
      toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
  });

  // 1. Load Product Data & Check Cart Status
  useEffect(() => {
    const initPage = async () => {
      setLoading(true);
      let currentProduct = null;

      if (location.state?.product) {
        currentProduct = location.state.product;
        setProduct(currentProduct);
      } else if (id) {
        try {
          const response = await fetch(SummaryApi.productDetails.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ product_id: id })
          });
          const data = await response.json();
          if (data.status && data.data) {
            currentProduct = Array.isArray(data.data) ? data.data[0] : data.data;
            setProduct(currentProduct);
          }
        } catch (error) {
          console.error("Error fetching product", error);
        }
      }

      if (currentProduct && customerId) {
        await checkCartStatus(currentProduct.product_id);
      }
      
      setLoading(false);
    };

    initPage();
  }, [location.state, id, customerId]);

  // Helper: Check Cart Status
  const checkCartStatus = async (productId) => {
    try {
      const response = await fetch(SummaryApi.getCartItems.url, {
        method: SummaryApi.getCartItems.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({ customer_id: customerId })
      });
      const data = await response.json();
      
      if (data.status && Array.isArray(data.data)) {
        const cartItem = data.data.find(item => 
          item.product_id.toString() === productId.toString()
        );

        if (cartItem) {
          setIsInCart(true);
          setCartId(cartItem.cart_id);
          setQuantity(parseInt(cartItem.quantity));
        }
      }
    } catch (error) {
      console.error("Error checking cart", error);
    }
  };

  // 2. API: Update Cart Quantity
  const updateCartQuantityApi = async (newQty) => {
    setUpdatingCart(true);
    try {
      const response = await fetch(SummaryApi.updateCartItem.url, {
        method: SummaryApi.updateCartItem.method,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({
            cart_id: cartId,
            product_id: product.product_id,
            quantity: newQty,
            customer_id: customerId
        })
      });

      const data = await response.json();

      if (data.status) {
        setQuantity(newQty);
        // Toast for successful update
        Toast.fire({
            icon: 'success',
            title: 'Quantity updated'
        });
      } else {
        Toast.fire({
            icon: 'error',
            title: 'Failed to update'
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUpdatingCart(false);
    }
  };

  // 3. Handlers for + and -
  const handleIncrease = () => {
    const newQty = quantity + 1;
    if (isInCart) {
      updateCartQuantityApi(newQty);
    } else {
      setQuantity(newQty);
      // Optional: Toast for local state
      // Toast.fire({ icon: 'info', title: `Quantity: ${newQty}` });
    }
  };

  const handleDecrease = () => {
    if (quantity <= 1) return;
    const newQty = quantity - 1;
    if (isInCart) {
      updateCartQuantityApi(newQty);
    } else {
      setQuantity(newQty);
    }
  };

  // 4. Helper to construct Image URL
  const getImageUrl = (imgName) => {
    if (!imgName) return "https://via.placeholder.com/400";
    const domain = SummaryApi.backendDomain.replace(/\/$/, "");
    const imgPath = imgName.replace(/^\//, "");
    return `${domain}/product/${imgPath}`; 
  };

  // 5. FUNCTION: Add to Cart (Stays on Page)
  const handleAddToCart = async () => {
    if (!customerId) {
      Swal.fire('Please Login', 'You need to login to add items', 'warning')
        .then((res) => { if(res.isConfirmed) navigate('/login'); });
      return;
    }

    if (isInCart) {
      navigate('/cart');
      return;
    }

    try {
      const response = await fetch(SummaryApi.addToCart.url, {
        method: SummaryApi.addToCart.method,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({
            customer_id: customerId,
            product_id: product.product_id,
            quantity: quantity
        })
      });

      const data = await response.json();

      if (data.status) {
        setIsInCart(true);
        checkCartStatus(product.product_id);

        Swal.fire({
          icon: 'success',
          title: 'Added to Cart',
          text: 'What would you like to do next?',
          showCancelButton: true,
          confirmButtonText: 'Go to Cart',
          cancelButtonText: 'Continue Shopping',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#aaa'
        }).then((result) => {
           if(result.isConfirmed) navigate('/cart'); 
        });
      } else {
        Swal.fire('Error', data.message || 'Could not add to cart', 'error');
      }
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Something went wrong', 'error');
    }
  };

  // 6. FUNCTION: Buy Now (Directly goes to Cart/Checkout)
  const handleBuyNow = async () => {
    if (!customerId) {
        Swal.fire('Please Login', 'You need to login to purchase', 'warning')
          .then((res) => { if(res.isConfirmed) navigate('/login'); });
        return;
      }
  
      // If already in cart, just go there immediately
      if (isInCart) {
        navigate('/cart');
        return;
      }
  
      try {
        const response = await fetch(SummaryApi.addToCart.url, {
          method: SummaryApi.addToCart.method,
          headers: {
              'Content-Type': 'application/json',
              'Authorization': SummaryApi.bearerToken
          },
          body: JSON.stringify({
              customer_id: customerId,
              product_id: product.product_id,
              quantity: quantity
          })
        });
  
        const data = await response.json();
  
        if (data.status) {
          setIsInCart(true);
          // Directly navigate to cart
          navigate('/cart');
        } else {
          Swal.fire('Error', data.message || 'Could not proceed', 'error');
        }
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Something went wrong', 'error');
      }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><FaSpinner className="animate-spin text-4xl text-blue-600"/></div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center">Product not found</div>;

  const mainImage = selectedImage || getImageUrl(product.product_img1);

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-500 hover:text-blue-600 mb-8 transition">
          <FaArrowLeft className="mr-2" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          
          {/* Left: Images */}
          <div className="flex flex-col items-center lg:items-start lg:order-1">
            <div className="bg-gradient-to-b from-blue-50 to-white rounded-3xl p-8 shadow-lg border border-blue-100 w-full flex justify-center">
              <img
                src={mainImage}
                alt={product.product_name}
                className="w-full max-w-md object-contain h-[400px] mix-blend-multiply"
              />
            </div>
            
            <div className="flex gap-4 mt-6">
              {[product.product_img1, product.product_img2, product.product_img3, product.product_img4]
                .filter(Boolean)
                .map((img, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setSelectedImage(getImageUrl(img))}
                    className={`border-2 rounded-lg p-2 cursor-pointer w-20 h-20 bg-white ${selectedImage === getImageUrl(img) ? 'border-blue-600' : 'border-gray-200'}`}
                  >
                    <img src={getImageUrl(img)} className="w-full h-full object-contain" alt="thumbnail" />
                  </div>
              ))}
            </div>
          </div>

          {/* Right: Info */}
          <div className="space-y-8 lg:order-2">
            <div>
              <div className="flex items-center gap-2 mb-2">
                 <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                   {product.product_code || 'Product'}
                 </span>
                 <div className="flex text-yellow-400 text-sm">
                   {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className={i < (product.rating || 4) ? "" : "text-gray-300"} />
                   ))}
                 </div>
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
                {product.product_name}
              </h1>
              
              <h3 className="text-xl text-gray-500 mt-2 font-medium">
                {product.sub_title}
              </h3>

              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                {product.product_description}
              </p>
            </div>

            <div className="flex items-baseline gap-4">
              <span className="text-4xl font-bold text-blue-600">
                ₹ {product.after_discount_price}
              </span>
              {parseFloat(product.price) > parseFloat(product.after_discount_price) && (
                <span className="text-2xl text-gray-400 line-through">
                  ₹ {product.price}
                </span>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-6">
              <label className="text-lg font-medium text-gray-700">Quantity:</label>
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                <button
                  onClick={handleDecrease}
                  disabled={updatingCart || quantity <= 1}
                  className="px-4 py-2 hover:bg-gray-200 transition font-bold disabled:opacity-50"
                >
                  -
                </button>
                
                <div className="px-6 py-2 font-medium bg-white border-x border-gray-200 min-w-[3rem] text-center relative">
                   {updatingCart ? <FaSpinner className="animate-spin inline text-blue-600"/> : quantity}
                </div>
                
                <button
                  onClick={handleIncrease}
                  disabled={updatingCart}
                  className="px-4 py-2 hover:bg-gray-200 transition font-bold disabled:opacity-50"
                >
                  +
                </button>
              </div>
              {isInCart && <span className="text-green-600 text-sm font-medium"><FaCheck className="inline"/> Item in Cart</span>}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 text-white font-semibold py-4 px-8 rounded-full flex items-center justify-center gap-3 transition shadow-lg hover:shadow-xl transform hover:-translate-y-1 
                  ${isInCart ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                    <FaShoppingCart />
                    {isInCart ? "Go to Cart" : "Add to Cart"}
                </button>

                {/* Buy Now Button */}
                <button
                  onClick={handleBuyNow}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold py-4 px-8 rounded-full flex items-center justify-center gap-3 transition shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                    <FaBolt />
                    Buy Now
                </button>
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-12 bg-gray-50 rounded-3xl p-10 lg:p-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Product Details</h3>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {product.product_description}
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Highlights</h3>
            <ul className="space-y-4 text-gray-600">
              <li className="flex items-start gap-3">
                <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                <span>Premium Quality Ingredients</span>
              </li>
              {product.specifications && (
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                    <span>{product.specifications}</span>
                  </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;